import React, { useEffect, useState } from "react";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ClientRecord, InvoiceRecord, InvoiceStatus } from "../repositories/types";
import { Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem } from "@mui/material";
import ConfirmationAlert from "./ConfirmationAlert";

interface InvoiceActionsProps {
  clients: ClientRecord[];
  selectedInvoices: InvoiceRecord[];
  createInvoices: () => void;
  deleteInvoices: () => void;
  sendInvoices: () => void;
  markInvoicesAs: (status: InvoiceStatus, paidAt?: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      "flex-wrap": "wrap",
      "justify-content": "center",
    },
  })
);

const InvoiceActions: React.FC<InvoiceActionsProps> = (props: InvoiceActionsProps) => {
  const classes = useStyles();
  const [selectedInvoices, setSelectedInvoices] = useState(props.selectedInvoices);
  const [createDisabled, setCreateDisabled] = useState(false);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [markAsDisabled, setMarkAsDisabled] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [removeMarkAsCancelled, setRemoveMarkAsCancelled] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [paidAtDialogOpen, setPaidAtDialogOpen] = useState(false);
  const [paidAtDate, setPaidAtDate] = useState<Date | null>(new Date());
  const { createInvoices, deleteInvoices, sendInvoices, markInvoicesAs } = props;

  useEffect(() => {
    setSelectedInvoices(props.selectedInvoices);
    setCreateDisabled(false);
    setSendDisabled(false);
    setDeleteDisabled(false);
    setMarkAsDisabled(false);

    //Disable the create button if any selected have already been created
    if (props.selectedInvoices.filter((i) => i.status !== undefined).length > 0) {
      setCreateDisabled(true);
    }

    //Disable the send button if any selected aren't in the "CREATED" state
    if (
      props.selectedInvoices.filter((i) => i.status !== InvoiceStatus.CREATED && i.status !== InvoiceStatus.SENT)
        .length > 0
    ) {
      setSendDisabled(true);
    }

    // Disable the delete button if any selected have not been created
    if (props.selectedInvoices.filter((i) => i.status === undefined || i.status !== InvoiceStatus.CREATED).length > 0) {
      setDeleteDisabled(true);
      setRemoveMarkAsCancelled(true);
    }

    // Disable all actions if any have been voided
    if (
      props.selectedInvoices.filter(
        (i) =>
          i.status === InvoiceStatus.VOIDED ||
          i.status === InvoiceStatus.VOIDING ||
          i.status === InvoiceStatus.CANCELLED
      ).length > 0
    ) {
      setMarkAsDisabled(true);
    }

    if (props.selectedInvoices.filter((i) => i.status !== InvoiceStatus.SENT).length > 0) {
      setRemoveMarkAsCancelled(false);
    }
  }, [props]);

  const handleMarkAsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMarkAs = (status: InvoiceStatus) => {
    setMenuAnchorEl(null);
    if (status === InvoiceStatus.VOIDING) {
      setConfirmationOpen(true);
      return;
    }
    if (status === InvoiceStatus.PAID) {
      setPaidAtDate(new Date());
      setPaidAtDialogOpen(true);
      return;
    }
    markInvoicesAs(status);
  };

  const confirmCancel = () => {
    markInvoicesAs(InvoiceStatus.VOIDING);
    setConfirmationOpen(false);
  };

  const rejectCancel = () => {
    setConfirmationOpen(false);
    return;
  };

  const confirmPaidAt = () => {
    markInvoicesAs(InvoiceStatus.PAID, format(paidAtDate ?? new Date(), "yyyy-MM-dd"));
    setPaidAtDialogOpen(false);
  };

  const rejectPaidAt = () => {
    setPaidAtDialogOpen(false);
  };

  return (
    <div className={classes.root}>
      <ConfirmationAlert
        open={confirmationOpen}
        title="Are you sure you want to void this invoice?"
        message="Once voided you will have to create a new invoice. Do you wish to continue?"
        confirmFunction={confirmCancel}
        rejectFunction={rejectCancel}
      />
      <Dialog open={paidAtDialogOpen} disableEscapeKeyDown>
        <DialogTitle id="paid-at-dialog">Mark invoice(s) as paid</DialogTitle>
        <DialogContent>
          <DatePicker
            format="dd/MM/yyyy"
            label="Date Paid"
            closeOnSelect
            value={paidAtDate}
            onChange={setPaidAtDate}
            maxDate={new Date()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={rejectPaidAt}>Cancel</Button>
          <Button onClick={confirmPaidAt}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ButtonGroup
        color="secondary"
        variant="contained"
        aria-label="invoice actions"
        disabled={selectedInvoices.length === 0}
      >
        <Button onClick={createInvoices} disabled={createDisabled}>
          Create
        </Button>
        <Button onClick={sendInvoices} disabled={sendDisabled}>
          Send
        </Button>
        <Button onClick={deleteInvoices} disabled={deleteDisabled}>
          Delete
        </Button>
        <Button onClick={handleMarkAsClick} disabled={markAsDisabled}>
          Mark As...
        </Button>
      </ButtonGroup>
      <Menu
        id="mark-as-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => handleMarkAs(InvoiceStatus.PAID)}>PAID</MenuItem>
        {removeMarkAsCancelled && <MenuItem onClick={() => handleMarkAs(InvoiceStatus.VOIDING)}>VOIDED</MenuItem>}
        <MenuItem onClick={() => handleMarkAs(InvoiceStatus.SENT)}>SENT</MenuItem>
      </Menu>
    </div>
  );
};

export default InvoiceActions;
